import type { RefCallback, RefObject } from 'react';
import { useCallback } from 'react';

type PossibleRef<T> = RefCallback<T> | RefObject<T | null> | null | undefined;

// biome-ignore lint/suspicious/noConfusingVoidType: suppressed due to migration
const setRef = <T>(ref: PossibleRef<T>, value: T | null): (() => void) | void => {
    if (typeof ref === 'function') {
        return ref(value);
    }
    if (ref !== null && ref !== undefined) {
        ref.current = value;
    }
};

export const useComposedRefs = <T>(...refs: readonly PossibleRef<T>[]): RefCallback<T> => {
    return useCallback(
        (node: T | null) => {
            const cleanups = refs.map((ref) => setRef(ref, node));
            const hasCleanup = cleanups.some((cleanup) => typeof cleanup === 'function');

            if (hasCleanup) {
                return () => {
                    cleanups.forEach((cleanup, i) => {
                        if (typeof cleanup === 'function') {
                            cleanup();
                        } else {
                            setRef(refs[i], null);
                        }
                    });
                };
            }

            return undefined;
        },
        // biome-ignore lint/correctness/useExhaustiveDependencies: suppressed due to migration
        refs,
    );
};
